<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Supplier',
  },
})

const { can } = useAbility()

// ─── State ────────────────────────────────────────────────────────────────────
const search = ref('')
const filterActive = ref(null)
const page = ref(1)
const perPage = ref(20)
const loading = ref(false)
const suppliers = ref([])
const totalItems = ref(0)

const deleteDialog = ref(false)
const selectedSupplier = ref(null)
const deleteLoading = ref(false)
const deleteMessage = ref('')

// ─── Headers ──────────────────────────────────────────────────────────────────
const headers = [
  { title: 'Nama Supplier', key: 'name', sortable: false },
  { title: 'Contact Person', key: 'contact_person', sortable: false },
  { title: 'Telepon', key: 'phone', sortable: false },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'Status', key: 'is_active', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]

const activeOptions = [
  { title: 'Semua', value: null },
  { title: 'Aktif', value: 1 },
  { title: 'Nonaktif', value: 0 },
]

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchSuppliers = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value
    if (filterActive.value !== null) query.is_active = filterActive.value

    // ofetch return body langsung — pattern wajib: response?.data ?? response
    const response = await useApi('/api/v1/suppliers', { query }).get()
    const body = response?.data ?? response

    suppliers.value = body?.data ?? []
    totalItems.value = body?.meta?.total ?? 0
  } catch (err) {
    console.error('[Suppliers] fetch error', err)
    suppliers.value = []
  } finally {
    loading.value = false
  }
}

watch([search, filterActive, page], fetchSuppliers, { immediate: true })

// ─── Delete ───────────────────────────────────────────────────────────────────
const confirmDelete = (supplier) => {
  selectedSupplier.value = supplier
  deleteMessage.value = ''
  deleteDialog.value = true
}

const doDelete = async () => {
  deleteLoading.value = true
  try {
    const response = await useApi(`/api/v1/suppliers/${selectedSupplier.value.id}`).delete()
    const body = response?.data ?? response

    deleteMessage.value = body?.message ?? 'Berhasil.'
    deleteDialog.value = false
    fetchSuppliers()
  } catch (err) {
    const errBody = err?.data ?? err?.response?._data
    deleteMessage.value = errBody?.message ?? 'Gagal menghapus supplier.'
    console.error('[Suppliers] delete error', err)
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardText class="d-flex flex-wrap gap-4 align-center justify-space-between">
          <!-- Filters -->
          <div class="d-flex gap-3 flex-wrap align-center">
            <VTextField
              v-model="search"
              placeholder="Cari nama supplier..."
              prepend-inner-icon="ri-search-line"
              density="compact"
              hide-details
              clearable
              style="min-width: 220px"
            />
            <VSelect
              v-model="filterActive"
              :items="activeOptions"
              label="Status"
              density="compact"
              hide-details
              style="min-width: 130px"
            />
          </div>

          <VBtn
            v-if="can('create', 'Supplier')"
            :to="{ name: 'purchase-suppliers-create' }"
            prepend-icon="ri-add-line"
          >
            Tambah Supplier
          </VBtn>
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:page="page"
          :headers="headers"
          :items="suppliers"
          :items-length="totalItems"
          :items-per-page="perPage"
          :loading="loading"
          no-data-text="Belum ada supplier."
        >
          <template #item.is_active="{ item }">
            <VChip
              :color="item.is_active ? 'success' : 'default'"
              size="small"
            >
              {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <IconBtn
                v-if="can('update', 'Supplier')"
                :to="{ name: 'purchase-suppliers-id-edit', params: { id: item.id } }"
              >
                <VIcon icon="ri-pencil-line" />
                <VTooltip activator="parent">Edit</VTooltip>
              </IconBtn>
              <IconBtn
                v-if="can('delete', 'Supplier')"
                color="error"
                @click="confirmDelete(item)"
              >
                <VIcon icon="ri-delete-bin-line" />
                <VTooltip activator="parent">Hapus</VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>

  <!-- Confirm Delete Dialog -->
  <VDialog v-model="deleteDialog" max-width="420" persistent>
    <VCard>
      <VCardTitle class="pt-5 px-5">Hapus Supplier?</VCardTitle>
      <VCardText class="px-5">
        Yakin ingin menghapus <strong>{{ selectedSupplier?.name }}</strong>?
        <br>
        <span class="text-caption text-medium-emphasis">
          Jika supplier sudah memiliki Purchase Order, supplier akan dinonaktifkan (bukan dihapus permanen).
        </span>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn variant="tonal" @click="deleteDialog = false">Batal</VBtn>
        <VBtn color="error" :loading="deleteLoading" @click="doDelete">Hapus</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
